/**
 * Cascading Country → State → City selector backed by the `country-state-city`
 * dataset (same source & version apps/web uses in its profile form). Picking a
 * country populates its states; picking a state populates its cities. Values
 * are stored as human-readable names (not ISO codes) to match the
 * `CreateCustomerData`/`UpdateCustomerData` address contract, which keeps
 * `country` / `state` / `city` as plain strings.
 *
 * Admin's plain Shadcn `Select` has no search box, and the country/city lists
 * here run into the hundreds of entries, so each field is a searchable
 * combobox built from the existing `Command` + `Popover` primitives.
 */

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Country, State, City } from 'country-state-city';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface ComboboxOption {
  value: string;
  label: string;
}

interface LocationComboboxProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  disabled?: boolean;
  error?: string;
}

function LocationCombobox({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled,
  error,
}: LocationComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((option) => option.value === value);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-between font-normal',
              !selected && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            <span className="truncate">{selected ? selected.label : placeholder}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export interface LocationSelectProps {
  country: string;
  state: string;
  city: string;
  onCountryChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onCityChange: (value: string) => void;
  errors?: { country?: string; state?: string; city?: string };
  /** Disable all three selects (e.g. while a mutation is pending). */
  disabled?: boolean;
  className?: string;
}

export function LocationSelect({
  country,
  state,
  city,
  onCountryChange,
  onStateChange,
  onCityChange,
  errors,
  disabled = false,
  className,
}: LocationSelectProps) {
  const countries = React.useMemo(() => Country.getAllCountries(), []);
  const selectedCountry = React.useMemo(
    () => countries.find((c) => c.name === country) ?? null,
    [countries, country]
  );

  const states = React.useMemo(
    () => (selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode) : []),
    [selectedCountry]
  );
  const selectedState = React.useMemo(
    () => states.find((s) => s.name === state) ?? null,
    [states, state]
  );

  const cities = React.useMemo(
    () =>
      selectedCountry && selectedState
        ? City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode)
        : [],
    [selectedCountry, selectedState]
  );

  const countryOptions = React.useMemo(
    () => countries.map((c) => ({ value: c.name, label: `${c.flag} ${c.name}` })),
    [countries]
  );
  const stateOptions = React.useMemo(
    () => states.map((s) => ({ value: s.name, label: s.name })),
    [states]
  );
  const cityOptions = React.useMemo(
    () => cities.map((c) => ({ value: c.name, label: c.name })),
    [cities]
  );

  // Selecting a parent clears its dependents so a record never carries a
  // state/city that doesn't belong to the chosen country.
  const handleCountryChange = (value: string) => {
    onCountryChange(value);
    onStateChange('');
    onCityChange('');
  };
  const handleStateChange = (value: string) => {
    onStateChange(value);
    onCityChange('');
  };

  return (
    <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-3', className)}>
      <LocationCombobox
        id="country"
        label="Country"
        value={country}
        onChange={handleCountryChange}
        options={countryOptions}
        placeholder="Select country"
        searchPlaceholder="Search country..."
        emptyText="No country found"
        disabled={disabled}
        error={errors?.country}
      />
      <LocationCombobox
        id="state"
        label="State"
        value={state}
        onChange={handleStateChange}
        options={stateOptions}
        placeholder={
          !selectedCountry
            ? 'Select country first'
            : states.length === 0
              ? 'No states available'
              : 'Select state'
        }
        searchPlaceholder="Search state..."
        emptyText="No state found"
        disabled={disabled || !selectedCountry || states.length === 0}
        error={errors?.state}
      />
      <LocationCombobox
        id="city"
        label="City"
        value={city}
        onChange={onCityChange}
        options={cityOptions}
        placeholder={
          !selectedState
            ? 'Select state first'
            : cities.length === 0
              ? 'No cities available'
              : 'Select city'
        }
        searchPlaceholder="Search city..."
        emptyText="No city found"
        disabled={disabled || !selectedState || cities.length === 0}
        error={errors?.city}
      />
    </div>
  );
}
